/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Query parameter the shell apps put on the iframe URL to tell the Web App
 * which app version it is running as — e.g. `?x-app-version=v1.0.461`.
 */
export const APP_VERSION_QUERY_PARAM: string = "x-app-version";

/**
 * Only plain version tokens are accepted. The value ends up in
 * `document.cookie`, so anything containing `;`, `=` or whitespace is
 * rejected rather than allowed to inject extra cookie attributes.
 */
const APP_VERSION_PATTERN: RegExp = /^[A-Za-z0-9][A-Za-z0-9._-]*$/;

/**
 * The one validation rule for an app version token, wherever the value came
 * from — the shell's `x-app-version` query param, or the debug-only runtime
 * override (see `src/devtools/appVersionOverride.ts`). Both end up in the same
 * cookie via `setAppVersionCookie()`, so both must clear the same bar: a
 * leading alphanumeric followed by alphanumerics, `.`, `_` or `-`, and nothing
 * else. That rejects `; Domain=evil.com`, whitespace and the un-substituted
 * `__WEB_APP_VERSION__` placeholder alike.
 *
 * Exported rather than inlined so a second caller cannot drift into its own
 * near-copy of the rule.
 */
export function isValidAppVersion(version: string): boolean {
  return APP_VERSION_PATTERN.test(version);
}

/**
 * Returns the app version the shell app set as `x-app-version` on the iframe
 * URL, or `null` when the parameter is absent, empty or malformed.
 */
export function getAppVersionFromUrl(): string | null {
  const params: URLSearchParams = new URLSearchParams(window.location.search);
  const version: string | null = params.get(APP_VERSION_QUERY_PARAM);
  if (version === null) {
    return null;
  }
  const trimmedVersion: string = version.trim();
  return isValidAppVersion(trimmedVersion) ? trimmedVersion : null;
}
